import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Trash2, Palette, Upload, Video, Image as ImageIcon, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import ThemeParticles from '../ThemeParticles';

const PARTICLE_OPTIONS = [
  { value: '', label: 'Nenhum' },
  { value: 'neve', label: '❄️ Neve' },
  { value: 'confete', label: '🎉 Confete' },
  { value: 'baloes', label: '🎈 Balões' },
  { value: 'halloween', label: '🎃 Halloween' },
  { value: 'primavera', label: '🌸 Primavera' },
];
const PARTICLE_LABEL = { neve: '❄️ neve', confete: '🎉 confete', baloes: '🎈 balões',
  halloween: '🎃 halloween', primavera: '🌸 primavera' };
const PREVIEW_BG = {
  neve: '#1e3a5f',
  halloween: '#1a1030',
  primavera: 'linear-gradient(160deg, #cfeadf 0%, #f7f2d8 45%, #fde3ee 100%)',
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 1024 * 1024;
const DEFAULT_FIT = { x: 50, y: 50, size: 58 };

// Gradientes prontos para as barras (com efeito de luz passando)
const GRADIENT_PRESETS = {
  gold: 'linear-gradient(90deg, #8a5a00 0%, #d4a017 20%, #ffd700 45%, #fff3b0 50%, #ffd700 55%, #d4a017 80%, #8a5a00 100%)',
  silver: 'linear-gradient(90deg, #4b5563 0%, #9ca3af 20%, #d1d5db 45%, #ffffff 50%, #d1d5db 55%, #9ca3af 80%, #4b5563 100%)',
};
const detectBarMode = (val) => {
  if (!val) return 'solid';
  if (val === GRADIENT_PRESETS.gold) return 'gold';
  if (val === GRADIENT_PRESETS.silver) return 'silver';
  if (val.includes('gradient')) return 'custom';
  return 'solid';
};
const extractGradColors = (val) => {
  const m = (val || '').match(/#[0-9a-fA-F]{6}/g) || [];
  return [m[0] || '#9333ea', m[m.length - 1] || '#2563eb'];
};
const barValue = (mode, solid, g1, g2) => {
  if (mode === 'gold' || mode === 'silver') return GRADIENT_PRESETS[mode];
  if (mode === 'custom') return `linear-gradient(90deg, ${g1}, ${g2})`;
  return solid;
};
const BAR_MODES = [
  ['solid', '🎨 Sólida'],
  ['gold', '🥇 Dourado'],
  ['silver', '🥈 Prata'],
  ['custom', '🌈 Gradiente'],
];
const SAMPLE_PHOTO = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">' +
  '<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">' +
  '<stop offset="0" stop-color="#fb923c"/><stop offset="1" stop-color="#7c3aed"/></linearGradient></defs>' +
  '<rect width="200" height="200" fill="url(#g)"/>' +
  '<circle cx="100" cy="78" r="34" fill="#ffffff" opacity="0.9"/>' +
  '<ellipse cx="100" cy="165" rx="58" ry="42" fill="#ffffff" opacity="0.9"/>' +
  '<text x="100" y="196" font-size="20" font-family="Arial" font-weight="bold" fill="#7c3aed" text-anchor="middle">FOTO</text></svg>'
);

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}`,
});
const resolveUrl = (u) => (u && u.startsWith('/') ? `${BACKEND_URL}${u}` : u);

const ThemeManagement = () => {
  const [themes, setThemes] = useState([]);
  const [name, setName] = useState('');
  const [verticalFile, setVerticalFile] = useState(null);
  const [horizontalFile, setHorizontalFile] = useState(null);
  const [frameUrl, setFrameUrl] = useState(null);
  const [fit, setFit] = useState(DEFAULT_FIT);
  const [liveColorOn, setLiveColorOn] = useState(false);
  const [liveColor, setLiveColor] = useState('#9333ea');
  const [liveMode, setLiveMode] = useState('solid');
  const [liveGrad1, setLiveGrad1] = useState('#9333ea');
  const [liveGrad2, setLiveGrad2] = useState('#2563eb');
  const [buyersColorOn, setBuyersColorOn] = useState(false);
  const [buyersColor, setBuyersColor] = useState('#9333ea');
  const [buyersMode, setBuyersMode] = useState('solid');
  const [buyersGrad1, setBuyersGrad1] = useState('#9333ea');
  const [buyersGrad2, setBuyersGrad2] = useState('#2563eb');
  const [particles, setParticles] = useState('');
  const [overlayOpacity, setOverlayOpacity] = useState(25);
  const [overlayMode, setOverlayMode] = useState('light');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingFrame, setUploadingFrame] = useState(false);
  const [progress, setProgress] = useState('');

  const load = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/themes/`, { headers: authHeaders() });
      setThemes(res.data || []);
    } catch (e) {
      toast.error('Erro ao carregar temas');
    }
  };

  useEffect(() => { load(); }, []);

  const uploadFile = async (file, label, kind = 'video') => {
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const total = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < total; i++) {
      setProgress(`Enviando ${label}: ${Math.round(((i + 1) / total) * 100)}%`);
      const slice = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result.split(',')[1]);
        r.onerror = reject;
        r.readAsDataURL(slice);
      });
      await axios.post(`${BACKEND_URL}/api/themes/upload-chunk`, {
        upload_id: uploadId, chunk_index: i, total_chunks: total,
        data_b64: b64, content_type: file.type || 'video/mp4',
      }, { headers: authHeaders() });
    }
    const done = await axios.post(`${BACKEND_URL}/api/themes/upload-complete`,
      { upload_id: uploadId, kind }, { headers: authHeaders() });
    return done.data;
  };

  const handleFrameSelect = async (file) => {
    if (!file) { setFrameUrl(null); setFit(DEFAULT_FIT); return; }
    setUploadingFrame(true);
    try {
      const res = await uploadFile(file, 'moldura', 'frame');
      setFrameUrl(res.media_url);
      setFit(res.suggested_fit || DEFAULT_FIT);
      if (res.warning) toast.warning(res.warning);
      else toast.success('Moldura calibrada! Confira o encaixe no preview 👇');
    } catch (e) {
      toast.error('Erro na moldura: ' + (e.response?.data?.detail || e.message));
      setFrameUrl(null);
    } finally {
      setUploadingFrame(false); setProgress('');
    }
  };

  const resetForm = () => {
    setName(''); setVerticalFile(null); setHorizontalFile(null);
    setFrameUrl(null); setFit(DEFAULT_FIT);
    setLiveColorOn(false); setLiveColor('#9333ea'); setLiveMode('solid'); setLiveGrad1('#9333ea'); setLiveGrad2('#2563eb');
    setBuyersColorOn(false); setBuyersColor('#9333ea'); setBuyersMode('solid'); setBuyersGrad1('#9333ea'); setBuyersGrad2('#2563eb');
    setParticles(''); setEditing(null);
    setOverlayOpacity(25);
    setOverlayMode('light');
    document.querySelectorAll('[data-testid="theme-management"] input[type=file]').forEach(i => { i.value = ''; });
  };

  const handleEdit = (t) => {
    setEditing(t);
    setName(t.name || '');
    setFrameUrl(t.avatar_frame_url || null);
    setFit(t.frame_fit || DEFAULT_FIT);
    setLiveColorOn(!!t.live_bar_color);
    const lm = detectBarMode(t.live_bar_color);
    setLiveMode(lm);
    if (lm === 'solid') setLiveColor(t.live_bar_color || '#9333ea');
    if (lm === 'custom') { const [g1, g2] = extractGradColors(t.live_bar_color); setLiveGrad1(g1); setLiveGrad2(g2); }
    setBuyersColorOn(!!t.buyers_bar_color);
    const bm = detectBarMode(t.buyers_bar_color);
    setBuyersMode(bm);
    if (bm === 'solid') setBuyersColor(t.buyers_bar_color || '#9333ea');
    if (bm === 'custom') { const [g1, g2] = extractGradColors(t.buyers_bar_color); setBuyersGrad1(g1); setBuyersGrad2(g2); }
    setParticles(t.particles || '');
    setOverlayOpacity(t.overlay_opacity ?? 25);
    setOverlayMode(t.overlay_mode === 'dark' ? 'dark' : 'light');
    setVerticalFile(null); setHorizontalFile(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const liveBarValue = barValue(liveMode, liveColor, liveGrad1, liveGrad2);
  const buyersBarValue = barValue(buyersMode, buyersColor, buyersGrad1, buyersGrad2);

  const handleCreate = async () => {
    if (!name.trim()) return toast.error('Dê um nome ao tema');
    if (!editing && !verticalFile && !horizontalFile && !frameUrl && !liveColorOn && !buyersColorOn && !particles) {
      return toast.error('Personalize ao menos um item (vídeo, moldura, cor ou efeito)');
    }
    setSaving(true);
    try {
      const vUrl = verticalFile ? (await uploadFile(verticalFile, 'vídeo vertical')).media_url : null;
      const hUrl = horizontalFile ? (await uploadFile(horizontalFile, 'vídeo horizontal')).media_url : null;
      if (editing) {
        setProgress('Salvando alterações...');
        const payload = {
          name: name.trim(),
          avatar_frame_url: frameUrl,
          frame_fit: frameUrl ? fit : null,
          live_bar_color: liveColorOn ? liveBarValue : null,
          buyers_bar_color: buyersColorOn ? buyersBarValue : null,
          particles: particles || null,
          overlay_opacity: overlayOpacity,
          overlay_mode: overlayMode,
        };
        if (vUrl) payload.video_vertical_url = vUrl;
        if (hUrl) payload.video_horizontal_url = hUrl;
        await axios.patch(`${BACKEND_URL}/api/themes/${editing.id}`, payload, { headers: authHeaders() });
        toast.success(`Tema "${name.trim()}" atualizado! ✏️`);
      } else {
        setProgress('Criando tema...');
        await axios.post(`${BACKEND_URL}/api/themes/`, {
          name: name.trim(),
          video_vertical_url: vUrl,
          video_horizontal_url: hUrl,
          avatar_frame_url: frameUrl,
          frame_fit: frameUrl ? fit : null,
          live_bar_color: liveColorOn ? liveBarValue : null,
          buyers_bar_color: buyersColorOn ? buyersBarValue : null,
          particles: particles || null,
          overlay_opacity: overlayOpacity,
          overlay_mode: overlayMode,
        }, { headers: authHeaders() });
        toast.success('Tema criado! Ative-o quando quiser usar.');
      }
      resetForm();
      load();
    } catch (e) {
      toast.error('Erro ao salvar tema: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false); setProgress('');
    }
  };

  const handleToggle = async (theme, active) => {
    try {
      await axios.put(`${BACKEND_URL}/api/themes/${theme.id}/activate?active=${active}`,
        {}, { headers: authHeaders() });
      toast.success(active ? `Tema "${theme.name}" ativado no site! 🎨` : 'Tema desativado');
      load();
    } catch (e) {
      toast.error('Erro ao alterar tema');
    }
  };

  const handleDelete = async (theme) => {
    if (!window.confirm(`Excluir o tema "${theme.name}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/themes/${theme.id}`, { headers: authHeaders() });
      toast.success('Tema excluído');
      load();
    } catch (e) {
      toast.error('Erro ao excluir');
    }
  };

  const photoPx = 220 * (fit.size / 100);

  return (
    <div className="space-y-4" data-testid="theme-management">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {editing ? <Pencil className="w-5 h-5 text-blue-500" /> : <Palette className="w-5 h-5 text-orange-500" />}
            {editing ? `Editando: ${editing.name}` : 'Criar Novo Tema'}
          </CardTitle>
          <p className="text-sm text-gray-500">
            {editing
              ? 'Altere o que quiser e salve — vídeos só mudam se você enviar arquivos novos'
              : 'Todos os itens são opcionais — o que ficar vazio mantém o visual padrão do site'}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input data-testid="theme-name-input" placeholder="Nome do tema (ex: Aniversário KAKO)"
                 value={name} onChange={(e) => setName(e.target.value)} />

          {/* Vídeos de fundo */}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="border rounded-lg p-3 space-y-1">
              <label className="text-sm font-medium flex items-center gap-1">
                <Video className="w-4 h-4" /> Vídeo VERTICAL — celular (opcional)
              </label>
              <input data-testid="theme-vertical-input" type="file" accept="video/mp4,video/webm"
                     className="text-sm w-full" onChange={(e) => setVerticalFile(e.target.files?.[0] || null)} />
            </div>
            <div className="border rounded-lg p-3 space-y-1">
              <label className="text-sm font-medium flex items-center gap-1">
                <Video className="w-4 h-4" /> Vídeo HORIZONTAL — computador (opcional)
              </label>
              <input data-testid="theme-horizontal-input" type="file" accept="video/mp4,video/webm"
                     className="text-sm w-full" onChange={(e) => setHorizontalFile(e.target.files?.[0] || null)} />
            </div>
          </div>

          {/* Clareamento/Escurecimento sobre o vídeo de fundo */}
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Camada sobre o vídeo de fundo</label>
              <span className="text-sm font-bold text-orange-600" data-testid="overlay-opacity-value">{overlayOpacity}%</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setOverlayMode('light')}
                className={`text-sm py-1.5 rounded-md border font-medium transition-colors ${overlayMode === 'light' ? 'bg-white border-orange-500 text-orange-600 ring-1 ring-orange-500' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'}`}
                data-testid="overlay-mode-light"
              >
                ☀️ Clarear
              </button>
              <button
                type="button"
                onClick={() => setOverlayMode('dark')}
                className={`text-sm py-1.5 rounded-md border font-medium transition-colors ${overlayMode === 'dark' ? 'bg-gray-900 border-gray-900 text-white ring-1 ring-gray-900' : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'}`}
                data-testid="overlay-mode-dark"
              >
                🌙 Escurecer
              </button>
            </div>
            <input
              type="range" min="0" max="100" step="5"
              value={overlayOpacity}
              onChange={(e) => setOverlayOpacity(parseInt(e.target.value))}
              className="w-full accent-orange-500"
              data-testid="overlay-opacity-slider"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>0% = vídeo puro (cores vivas)</span>
              <span>100% = fundo {overlayMode === 'dark' ? 'preto' : 'branco'}</span>
            </div>
            <div className="h-10 rounded border overflow-hidden relative"
                 style={{ background: 'linear-gradient(90deg, #7c3aed, #2563eb, #f59e0b)' }}>
              <div className="absolute inset-0" style={{ backgroundColor: `rgba(${overlayMode === 'dark' ? '0,0,0' : '255,255,255'},${overlayOpacity / 100})` }} />
              <span className={`absolute inset-0 flex items-center justify-center text-xs font-medium ${overlayMode === 'dark' && overlayOpacity > 40 ? 'text-white' : 'text-gray-800'}`}>Prévia do efeito</span>
            </div>
          </div>

          {/* Moldura + preview + ajuste */}
          <div className="border rounded-lg p-3 space-y-2">
            <label className="text-sm font-medium flex items-center gap-1">
              <ImageIcon className="w-4 h-4" /> Moldura de avatar — PNG com centro transparente (opcional)
            </label>
            <input data-testid="theme-frame-input" type="file" accept="image/png,image/webp"
                   className="text-sm w-full" disabled={uploadingFrame}
                   onChange={(e) => handleFrameSelect(e.target.files?.[0] || null)} />
            {uploadingFrame && <p className="text-sm text-orange-600">Enviando e calibrando a moldura...</p>}
            {frameUrl && (
              <div className="flex items-center justify-between">
                <p className="text-xs text-green-700">Moldura carregada ✓</p>
                <Button variant="ghost" size="sm" data-testid="frame-remove-button"
                        className="text-red-500 h-7"
                        onClick={() => { setFrameUrl(null); setFit(DEFAULT_FIT); }}>
                  <X className="w-3 h-3 mr-1" /> Remover moldura
                </Button>
              </div>
            )}
            {frameUrl && (
              <div className="flex flex-col sm:flex-row gap-4 items-center pt-2">
                <div className="relative flex-shrink-0 bg-gray-100 rounded-lg" style={{ width: 220, height: 220 }}
                     data-testid="frame-preview">
                  <img src={SAMPLE_PHOTO} alt="exemplo"
                       className="absolute rounded-full object-cover"
                       style={{ width: photoPx, height: photoPx, left: `${fit.x}%`, top: `${fit.y}%`,
                                transform: 'translate(-50%, -50%)' }} />
                  <img src={resolveUrl(frameUrl)} alt="moldura"
                       className="absolute inset-0 w-full h-full pointer-events-none" />
                </div>
                <div className="flex-1 w-full space-y-2">
                  <p className="text-xs text-gray-600 font-medium">Ajuste a foto até encaixar perfeito na moldura:</p>
                  <div>
                    <label className="text-xs text-gray-600">↔️ Posição horizontal</label>
                    <input data-testid="fit-x-slider" type="range" min="25" max="75" step="0.5" value={fit.x}
                           className="w-full" onChange={(e) => setFit({ ...fit, x: parseFloat(e.target.value) })} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">↕️ Posição vertical</label>
                    <input data-testid="fit-y-slider" type="range" min="25" max="75" step="0.5" value={fit.y}
                           className="w-full" onChange={(e) => setFit({ ...fit, y: parseFloat(e.target.value) })} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">🔍 Tamanho da foto</label>
                    <input data-testid="fit-size-slider" type="range" min="25" max="90" step="0.5" value={fit.size}
                           className="w-full" onChange={(e) => setFit({ ...fit, size: parseFloat(e.target.value) })} />
                  </div>
                  <Button variant="outline" size="sm" data-testid="fit-reset-button"
                          onClick={() => setFit(DEFAULT_FIT)}>Voltar ao ajuste automático</Button>
                </div>
              </div>
            )}
          </div>

          {/* Cores das barras */}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">🔴 Cor da barra AO VIVO (opcional)</label>
                <Switch data-testid="live-color-toggle" checked={liveColorOn} onCheckedChange={setLiveColorOn} />
              </div>
              {liveColorOn && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {BAR_MODES.map(([m, label]) => (
                      <button key={m} type="button" onClick={() => setLiveMode(m)}
                              data-testid={`live-mode-${m}`}
                              className={`px-2 py-1 rounded text-xs font-semibold border transition-colors ${liveMode === m ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    {liveMode === 'solid' && (
                      <input data-testid="live-color-input" type="color" value={liveColor}
                             className="w-10 h-10 rounded cursor-pointer border"
                             onChange={(e) => setLiveColor(e.target.value)} />
                    )}
                    {liveMode === 'custom' && (
                      <>
                        <input data-testid="live-grad1-input" type="color" value={liveGrad1}
                               className="w-10 h-10 rounded cursor-pointer border"
                               onChange={(e) => setLiveGrad1(e.target.value)} />
                        <input data-testid="live-grad2-input" type="color" value={liveGrad2}
                               className="w-10 h-10 rounded cursor-pointer border"
                               onChange={(e) => setLiveGrad2(e.target.value)} />
                      </>
                    )}
                    <div className="relative overflow-hidden flex-1 rounded py-2 px-3 text-white text-xs font-bold shadow"
                         style={{ background: liveBarValue, textShadow: '0 1px 2px rgba(0,0,0,.5)' }}>
                      {liveMode !== 'solid' && <div className="bar-light-sweep" />}
                      AO VIVO • prévia da barra
                    </div>
                  </div>
                  {liveMode !== 'solid' && <p className="text-xs text-amber-600">✨ Gradientes ganham efeito de luz passando na barra</p>}
                </div>
              )}
              {!liveColorOn && <p className="text-xs text-gray-500">Desligado = cor original (roxo/azul)</p>}
            </div>
            <div className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">🛒 Cor da barra de COMPRADORES (opcional)</label>
                <Switch data-testid="buyers-color-toggle" checked={buyersColorOn} onCheckedChange={setBuyersColorOn} />
              </div>
              {buyersColorOn && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {BAR_MODES.map(([m, label]) => (
                      <button key={m} type="button" onClick={() => setBuyersMode(m)}
                              data-testid={`buyers-mode-${m}`}
                              className={`px-2 py-1 rounded text-xs font-semibold border transition-colors ${buyersMode === m ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    {buyersMode === 'solid' && (
                      <input data-testid="buyers-color-input" type="color" value={buyersColor}
                             className="w-10 h-10 rounded cursor-pointer border"
                             onChange={(e) => setBuyersColor(e.target.value)} />
                    )}
                    {buyersMode === 'custom' && (
                      <>
                        <input data-testid="buyers-grad1-input" type="color" value={buyersGrad1}
                               className="w-10 h-10 rounded cursor-pointer border"
                               onChange={(e) => setBuyersGrad1(e.target.value)} />
                        <input data-testid="buyers-grad2-input" type="color" value={buyersGrad2}
                               className="w-10 h-10 rounded cursor-pointer border"
                               onChange={(e) => setBuyersGrad2(e.target.value)} />
                      </>
                    )}
                    <div className="relative overflow-hidden flex-1 rounded py-2 px-3 text-white text-xs font-bold shadow"
                         style={{ background: buyersBarValue, textShadow: '0 1px 2px rgba(0,0,0,.5)' }}>
                      {buyersMode !== 'solid' && <div className="bar-light-sweep" />}
                      Compras recentes • prévia da barra
                    </div>
                  </div>
                  {buyersMode !== 'solid' && <p className="text-xs text-amber-600">✨ Gradientes ganham efeito de luz passando na barra</p>}
                </div>
              )}
              {!buyersColorOn && <p className="text-xs text-gray-500">Desligado = cor original (roxo/azul)</p>}
            </div>
          </div>

          {/* Efeito de partículas */}
          <div className="border rounded-lg p-3 space-y-2">
            <label className="text-sm font-medium">✨ Efeito na tela (opcional)</label>
            <div className="flex flex-wrap gap-2">
              {PARTICLE_OPTIONS.map((o) => (
                <Button key={o.value || 'none'} variant={particles === o.value ? 'default' : 'outline'} size="sm"
                        data-testid={`particles-${o.value || 'none'}`}
                        className={particles === o.value ? 'bg-orange-500 hover:bg-orange-600' : ''}
                        onClick={() => setParticles(o.value)}>
                  {o.label}
                </Button>
              ))}
            </div>
            {particles && (
              <div className="relative rounded-lg overflow-hidden border h-32"
                   style={{ background: PREVIEW_BG[particles] || '#f5f0ff' }}
                   data-testid="particles-preview">
                <ThemeParticles type={particles} contained
                                count={particles === 'baloes' ? 6 : particles === 'halloween' ? 10
                                  : particles === 'primavera' ? 14 : 18} />
                <p className="absolute bottom-1 right-2 text-[10px] text-gray-400">prévia do efeito</p>
              </div>
            )}
          </div>

          {progress && <p className="text-sm text-orange-600 font-medium">{progress}</p>}
          <div className="flex gap-2">
            <Button data-testid="theme-create-button" onClick={handleCreate} disabled={saving || uploadingFrame}
                    className={editing ? 'bg-blue-600 hover:bg-blue-700' : 'bg-orange-500 hover:bg-orange-600'}>
              <Upload className="w-4 h-4 mr-2" />
              {saving ? 'Enviando...' : editing ? 'Salvar Alterações' : 'Criar Tema'}
            </Button>
            {editing && (
              <Button variant="outline" data-testid="theme-cancel-edit-button" onClick={resetForm} disabled={saving}>
                <X className="w-4 h-4 mr-1" /> Cancelar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Temas ({themes.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {themes.length === 0 && <p className="text-sm text-gray-500">Nenhum tema criado ainda.</p>}
          {themes.map((t) => (
            <div key={t.id} data-testid={`theme-row-${t.id}`}
                 className="flex items-center justify-between border rounded-lg p-3 gap-3">
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2">
                  {t.name}
                  {t.active && <Badge className="bg-green-100 text-green-800 border-green-300">ATIVO NO SITE</Badge>}
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
                  {t.video_vertical_url ? '📱 vertical ✓ ' : ''}{t.video_horizontal_url ? ' 🖥️ horizontal ✓' : ''}{t.avatar_frame_url ? ' 🖼️ moldura ✓' : ''}
                  {t.live_bar_color && <span className="inline-flex items-center gap-1"> 🔴 ao vivo <span className="inline-block w-3 h-3 rounded-full border" style={{ background: t.live_bar_color }} /></span>}
                  {t.buyers_bar_color && <span className="inline-flex items-center gap-1"> 🛒 compradores <span className="inline-block w-3 h-3 rounded-full border" style={{ background: t.buyers_bar_color }} /></span>}
                  {t.particles && <span> ✨ {PARTICLE_LABEL[t.particles] || t.particles}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Switch data-testid={`theme-toggle-${t.id}`} checked={!!t.active}
                        onCheckedChange={(v) => handleToggle(t, v)} />
                <Button data-testid={`theme-edit-${t.id}`} variant="ghost" size="icon"
                        onClick={() => handleEdit(t)}>
                  <Pencil className="w-4 h-4 text-blue-500" />
                </Button>
                <Button data-testid={`theme-delete-${t.id}`} variant="ghost" size="icon"
                        onClick={() => handleDelete(t)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default ThemeManagement;
